import differenceInDays from "date-fns/differenceInDays";
import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import prepareMeta from "../../../utils/prepare-meta";
import useAnalytics from "../../hooks/use-analytics";
import useCountryCode from "../../hooks/use-country-code";
import usePathParam from "../../hooks/use-path-params";
import fwclxs from "../../styles/full-width.module.css";
import { useQueryParamValue } from "../../hooks/use-query-param";
import useReservationService from "../../hooks/use-reservation-service";
import useScrollToTop from "../../hooks/use-scroll-to-top";
import { useSession, useUser } from "../../hooks/use-session";
import { useSnackbar } from "../../hooks/use-snackbar";
import useUtm from "../../hooks/use-utm";
import Form from "../Form";
import { Checkbox,  TextArea } from "../Form/Fields";
import SecurePayment from "../SecurePayment";
import CancellationPolicy from "../Checkout/CancellationPolicy";
import Header from "../Checkout/Header";
import { usePropertyConfigValues } from "../../hooks/use-property-config";
import { useStorageNumber } from "../../hooks/use-storage";
import { usePersistedCheckoutFormValue, CheckoutFormInputs } from "../Checkout/usePersistedCheckoutFormValue";
import CHECKOUT_SCHEMA from "../Checkout/checkout-schema";
import clxs from "../Checkout/checkout.module.css";
import useEligibilityPoints from "../../hooks/use-eligibility-points";
import { useLoginModal } from "../../hooks/use-login-modal";
import BookingBreakdown from "../Checkout/BookingBreakdown";
import useClx from "../../hooks/use-clx";
import useEnv from "../../hooks/use-env";
import ClusterSummary from "./ClusterSummary";
import useClusterDetail from "../../hooks/use-cluster-detail";
import useClusterBookingSummary from "../../hooks/use-cluster-booking-summary";

function CheckoutCluster () {
  useScrollToTop();

  const [qp] = useSearchParams(),
    push = useNavigate(),
    enqueueSnackbar = useSnackbar(),
    { utm } = useUtm(),
    { JUSTPAY_API_URL } = useEnv(),
    { service: reservationService } = useReservationService(),
    { track } = useAnalytics(),
    session = useSession(),
    showLoginFlowModal = useLoginModal(),
    ccx = useClx(
      fwclxs.container,
      fwclxs.containerPadding,
      clxs.checkOutContainer,
    ),
    formRef = useRef<HTMLDivElement>(null),
    { countryCode } = useCountryCode(),
    location = usePathParam(PATH, "location"),
    destination = usePathParam(PATH, "destination"),
    cluster_slug = usePathParam(PATH, "cluster_slug"),
    booking_id = useQueryParamValue("string", "booking_id"),
    config_booking_slugs = useQueryParamValue("string", "booking_slugs"),
    checkin_date = useQueryParamValue("date", "checkin_date"),
    checkout_date = useQueryParamValue("date", "checkout_date"),
    adult_count = useQueryParamValue("integer", "adult_count"),
    child_count = useQueryParamValue("integer", "child_count"),
    pre_book_meals = useQueryParamValue("boolean","pre_book_meals"),
    coupon_code = useQueryParamValue("string", COUPON_CODE_QUERY_KEY),
    error_payment_message = useQueryParamValue("string", "error"),
    offering_type_ids = useQueryParamValue("string", OFFERING_ID_KEY),
    { value: cid } = useStorageNumber("localStorage", "customer_id"),
    { clusterDetail } = useClusterDetail(cluster_slug, adult_count, checkin_date, checkout_date,offering_type_ids),
    { clusterBookingSummary } = useClusterBookingSummary(cluster_slug, adult_count, checkin_date, checkout_date,offering_type_ids),
    { configArgs, configMap } = usePropertyConfigValues(cluster_slug, clusterDetail?.configs ?? []),
    avail_loyalty_points = useQueryParamValue("boolean", AVAIL_LOYALTY_POINTS_QUERY_KEY),
    user = useUser(),
    tier = useQueryParamValue("string", "tier"),
    // In selected configs we are making required rooms to 1, since all configs are basically villas with id and are part of cluster bookin. By default it may come as 0.
    selected_configs = useMemo(
      () => configArgs.map(each => ({...each, required_rooms: 1})),
      [configArgs],
    ),
    { eligibilityPoints } = session ? useEligibilityPoints(checkin_date, checkout_date,cluster_slug,selected_configs,session,true) : {eligibilityPoints : null},
    // using custom-made hook to set form values in localstorage and setting data in form.
    {
      handleSetCheckoutFormValues: handleSetCheckoutFormValues,
      inputFormValues: localInputFormValues,
    } = usePersistedCheckoutFormValue(DEFAULT_INPUT_NAMES),
    defaultValue = useMemo(
      () => {
        const phoneNo = user?.phone_no.replace("+91", "") ?? "";

        return {
          cluster_slug: cluster_slug,
          checkin_date: checkin_date,
          checkout_date: checkout_date,
          adult_count: adult_count,
          pre_book_meals:pre_book_meals,
          child_count: child_count,
          full_name: user?.full_name || "",
          email: user?.email || "",
          phone_ext: (countryCode !== "IN" ? "+44" : "+91"),
          phone_no: phoneNo,
          message: localInputFormValues.message || "",
          config_args: configArgs,
          tnc: true,
        };
      },
      [user, countryCode],
    ),
    {
      viewLayout,
    } = useMemo(
      () => {
        return {
          viewLayout: clusterDetail?.view_layout || "standard",
          propertyName: clusterDetail?.name || "",
        };
      },
      [clusterDetail],
    ),
    totalGuests = useMemo(
      () => adult_count + child_count,
      [adult_count, child_count],
    ),
    totalNights = useMemo(
      () => {
        if (!checkin_date || !checkout_date) {
          return 0;
        }

        const diff = differenceInDays(checkout_date, checkin_date);

        return diff;
      },
      [checkin_date, checkout_date],
    ),
    // Check for cluster
    // isMultiConfig = viewLayout === "multi_config",

    handleDateChange = async () => {
      const updateQp = new URLSearchParams(qp);

      updateQp.set("focus", "rate_calculator");
      // Check for cluster - set avail loyalty point flag to false. 

      const search = `?${updateQp.toString()}`;

      return push({
        pathname: `/cluster-villas/${destination}/${location}/${cluster_slug}`,
        search: search,
      });
    },
    prepareVillaNameMessage = () => {
      if (!clusterBookingSummary) {
        return "Cluster Booking - ";
      }

      const bookingItems = clusterBookingSummary?.booking_item || [];
      const villaNames = bookingItems.map(item => item.villa_name).filter(Boolean);

      let villaNameMessage = "";
      if (villaNames.length === 1) {
        villaNameMessage = villaNames[0];
      } else if (villaNames.length === 2) {
        villaNameMessage = `${villaNames[0]} & ${villaNames[1]}`;
      } else if (villaNames.length > 2) {
        villaNameMessage = `${villaNames.slice(0, -1).join(", ")} & ${villaNames[villaNames.length - 1]}`;
      }

      return `Cluster Booking - ${villaNameMessage}`;
    },
    makePaymentFunction = async (value: any) => {
      
      if (!value) {
        return;
      }

      //Method of hook to set the values in localstorage if changed.
      handleSetCheckoutFormValues(value);

      const {
          // property_slug,
          checkin_date,
          checkout_date,
          // adult_count,
          // child_count,
          full_name,
          email,
          phone_ext,
          phone_no,
          message,
          config_args,
        } = value,
        phone = `${phone_ext} ${phone_no}`,
        meta = prepareMeta(utm),
        // LOHONO_BRAND = 1,
        ratingsPayload = JSON.parse(localStorage.getItem("ratingsPayload") || ""),
        trackingPayload: any = {
          ...value,
          ...meta,
          config_args: config_args,
          checkin_date: checkin_date.toISOString(),
          checkout_date: checkout_date.toISOString(),
          // tentativePrice: tentativePrice, //Get tentative price from booking breakdown.
          isLoyaltyPointsUsed: avail_loyalty_points,
          property_type: viewLayout,
          // loyaltyPointsValue: avail_loyalty_points ? tentativePrice?.breakdown?.loyalty_points_redeemed_value : 0,
          meal_plan_opt: pre_book_meals,
          ...ratingsPayload,
        };

        // Old Gtag Event
        track(
          "checkout_make_payment_clicked",
          trackingPayload,
        );
        
        //Send tier level in coupon code if no coupon code is used.
        // const tierLevel = tentativePrice.breakdown.discount_method;
        // let couponCodePayload = coupon_code;
        // if(!coupon_code || coupon_code === "NONE") {
        //   couponCodePayload = tierLevel.toUpperCase();
        // }

        const redirectUrl = window.location.href;

        const villaNameMessage = prepareVillaNameMessage();
        
        const updatedMessage = villaNameMessage + " \n\n " + message;

        const { error: e2, response: r2} = await reservationService.updateBooking({
            full_name: full_name,
            email: email,
            phone_no: phone,
            customer_note: updatedMessage,
            booking_id: booking_id,
            booking_slugs: config_booking_slugs.split("-"),
          },
          session,
        );

        if (e2) {
          enqueueSnackbar(e2.message, "error");
  
          track(
            "checkout_error",
            {
              ...trackingPayload,
              error: e2.message,
            },
          );
          return;
        }
  
        if (!r2) {
          enqueueSnackbar("unknown error", "error");
  
          track(
            "checkout_error",
            {
              ...trackingPayload,
              error: "unknown error",
            },
          );
          return;
        }
        
        if(!r2 || config_booking_slugs.length === 0) {
          enqueueSnackbar("Booking could not be created. Please reach out to our support team.", "error");
          return;
        }

        const { error: e1, response: r1} = await reservationService.clusterBookingPayment({
          booking_slugs: config_booking_slugs.split("-"),
          redirect_failure_url: redirectUrl,
        },
        session,
      )

      if (e1) {
        enqueueSnackbar(e1.message, "error");

        track(
          "checkout_error",
          {
            ...trackingPayload,
            error: e1.message,
          },
        );
        return;
      }

      if (!r1) {
        enqueueSnackbar("unknown error", "error");

        track(
          "checkout_error",
          {
            ...trackingPayload,
            error: "unknown error",
          },
        );
        return;
      }

      const { order_id } = r1;

      localStorage.setItem("trackingPayload", JSON.stringify(trackingPayload));

      //Firebase event name - payment_initiated
      track(
        "checkout_payment_initiated",
        trackingPayload,
      );

      //Prepare payment link based on the JustPay API URL. URL changes based on if the environment is staging or production.
      const tempPaymentLink = JUSTPAY_API_URL + "/payment-page/order/" + order_id;
      
      //Redirect to just pay payment page when make payment button is clicked.
      window.location.replace(tempPaymentLink);

    },
    handleMakePayment = (value: any) => {
      //If the user is not loggedin show login popup then allow to move to checkout page.
      if(!user) {
        const modalProps = {
          className: "",
          onSubmit: makePaymentFunction,
        }

        return showLoginFlowModal(modalProps).then((success: any) => {
          if(success) {
            makePaymentFunction(value);
          }
        })
      } else { //If the user is already logged in.
        return makePaymentFunction(value);
      }
    },
    handlePaymentError = () => {
      // If there was any error message in payment. Show snack bar.
      if (error_payment_message.length) {
        enqueueSnackbar(error_payment_message, "error");
        //Firebase event name - payment_failed
        const ratingsPayload = JSON.parse(localStorage.getItem("ratingsPayload") || "");
        track(
          "checkout_payment_failed",
          {
            cid,
            error: error_payment_message,
            // tentativePrice: tentativePrice, //Get the tentative price later from booking breakdwon.
            property_type: viewLayout,
            isLoyaltyPointsUsed: avail_loyalty_points,
            // loyaltyPointsValue: avail_loyalty_points ? tentativePrice?.breakdown?.loyalty_points_redeemed_value : 0,
            totalNights: totalNights,
            meal_plan_opt: pre_book_meals,
            ...ratingsPayload,
          },
        );
      }
    };

  // If payment fails due to any reason showing a snack bar with error after it's redirected here.
  useEffect(
    () => {
      handlePaymentError();
    },
    [],
  );

  if (!clusterDetail && !clusterBookingSummary) {
    return (
      <div className={clxs.placeholder}>
        &nbsp;
      </div>
    );
  }

  return (
    <div className={ccx}>
      <Header />
      <div className={clxs.container}>
        <ClusterSummary
          viewLayout={viewLayout}
          booking_item={clusterBookingSummary?.booking_item || []}
          checkinDate={checkin_date}
          checkoutDate={checkout_date}
          checkOutTime={clusterDetail?.check_out_time}
          checkInTime={clusterDetail?.check_in_time}
          adultCount={adult_count}
          preBookMeals={pre_book_meals}
          childCount={child_count}
          className={clxs.summary}
          onDateChange={handleDateChange} 
          configArgs={configArgs} 
          configMap={configMap}        
        />

        <div className={clxs.stickyContainer}>
          <BookingBreakdown
            bookingSlugs={config_booking_slugs.split("-")}
            viewLayout={viewLayout}
            formId={CHECKOUT_FORM_ID}
            couponCodeSearchKey={COUPON_CODE_QUERY_KEY}
            availLoyaltyPointsSearchKey={AVAIL_LOYALTY_POINTS_QUERY_KEY}
            calculatorId={PRICE_CALCULATOR_ID}
            propertySlug={cluster_slug}
            checkinDate={checkin_date}
            checkoutDate={checkout_date}
            adultCount={adult_count}
            childCount={child_count}
            couponCode={coupon_code}
            configs={configArgs}
            totalNights={totalNights}
            totalGuests={totalGuests}
            availLoyaltyPoints={avail_loyalty_points}
            className={clxs.breakdown}
            loyaltyPointsBalance={user?.loyalty_point_metadata.points_balance} 
            configMap={configMap}
            tier={tier}
            eligibilityPointsObj={eligibilityPoints}
            refundableSearchKey={REFUNDABLE_BOOKING_QUERY_KEY}
            quoteIdSearchKey={REFUNDABLE_BOOKING_QUOTEID_KEY}
            isOptedRefundableBooking={false}
            isDisplayProtectWidget={false}
            formRef={formRef}    
            isCluster={true}
            offering_type_ids={offering_type_ids}      
          >
            <Checkbox
              form={CHECKOUT_FORM_ID}
              name="tnc"
              className={clxs.tnc}
              color="primary"
              label={(
                <div className={clxs.tncContent}>
                  {"I have read and accept the "}
                  <a
                    href="/terms-and-conditions"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Terms & Conditions
                  </a>{" and "}
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    Privacy Policies
                  </a>
                </div>
              )}
            />
          </BookingBreakdown>
          <SecurePayment className={clxs.secureDesktop} />
        </div>

        <Form
          id={CHECKOUT_FORM_ID}
          className={clxs.details}
          defaultValue={defaultValue}
          validationSchema={CHECKOUT_SCHEMA}
          onSubmit={handleMakePayment}
          enableReinitialize={true}
        >
          <div className={clxs.detailsTitle}>
            Do you have any special notes or requests?
          </div>
          <div className={clxs.detailsSubtitle}>
            Tell us what you need—we’ll do our best to make it special!
          </div>
          <TextArea
            form={CHECKOUT_FORM_ID}
            name="message"
            //label="Message"
            placeholder="(Anniversary setup, extra pillows, vegan breakfast, airport pickup)"
            className={clxs.note}
            rows={4}
          />
          
          <div className={clxs.detailsDescription}>
            Special requests cannot be guaranteed but the accommodation will do its best to meet your needs, you can always add these after the booking is confirmed too!
          </div>
        </Form> 


        {/* Check -> breaking on page load -- Enabled now*/}

        <CancellationPolicy
          className={clxs.cancellation}
          policies={clusterDetail?.cancellation_policy || []}
        />  
      
      </div>
    </div>
  );
}

export default CheckoutCluster;

//Names of input field whose value is to be stored in local storage.
const DEFAULT_INPUT_NAMES: CheckoutFormInputs = {
  name: "full_name",
  phone_ext: "phone_ext",
  phone: "phone_no",
  message: "message",
};

const CHECKOUT_FORM_ID = "checkout";

const PRICE_CALCULATOR_ID = "checkout-cluster-rate-calculator";

const COUPON_CODE_QUERY_KEY = "coupon_code";

const AVAIL_LOYALTY_POINTS_QUERY_KEY = "avail_loyalty_points";

const REFUNDABLE_BOOKING_QUOTEID_KEY = "quote_id";

const REFUNDABLE_BOOKING_QUERY_KEY = "avail_refundable_booking";

const PATH = "/cluster-villas/:destination/:location/:cluster_slug/checkout";

const OFFERING_ID_KEY = "offering_type_ids";